// Shared helpers for the Monthly Client Work Planner.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api/work-planner/monthly`;

export class ApiError extends Error {
  constructor(message, status) { super(message); this.status = status; }
}

function qs(params = {}) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, v); });
  const s = p.toString();
  return s ? `?${s}` : "";
}

export async function mpApi(path, { method = "GET", body, params } = {}) {
  const res = await fetch(`${BASE}${path}${qs(params)}`, {
    method, credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.detail || `Request failed (${res.status})`, res.status);
  return data;
}
export const mpUrl = (path, params) => `${BASE}${path}${qs(params)}`;

// Service palette (keys match the server's catalogue).
export const SERVICE_META = {
  creatives: { label: "Creatives", short: "Creative", color: "#6366f1", soft: "bg-indigo-50 text-indigo-700 ring-indigo-200", bar: "bg-indigo-500", icon: "Image" },
  motion_graphic: { label: "Motion Graphic", short: "Motion", color: "#0ea5e9", soft: "bg-sky-50 text-sky-700 ring-sky-200", bar: "bg-sky-500", icon: "Wand2" },
  animated_video: { label: "Animated Video", short: "Animated", color: "#8b5cf6", soft: "bg-violet-50 text-violet-700 ring-violet-200", bar: "bg-violet-500", icon: "Clapperboard" },
  ai_video: { label: "AI Video", short: "AI Video", color: "#ec4899", soft: "bg-pink-50 text-pink-700 ring-pink-200", bar: "bg-pink-500", icon: "Sparkles" },
  reel_shoot: { label: "Reel Shoot", short: "Reel", color: "#f59e0b", soft: "bg-amber-50 text-amber-700 ring-amber-200", bar: "bg-amber-500", icon: "Video" },
  blog: { label: "Blog", short: "Blog", color: "#14b8a6", soft: "bg-teal-50 text-teal-700 ring-teal-200", bar: "bg-teal-500", icon: "FileText" },
};
// Live service list. Filled from the server (setServices) so admin-added services appear
// everywhere; mutated in place so every importer sees the same array.
export const SERVICE_KEYS = Object.keys(SERVICE_META);
const DEFAULT_KEYS = Object.keys(SERVICE_META);
export function setServices(list) {
  if (!Array.isArray(list) || !list.length) return;
  for (const s of list) {
    const base = DEFAULT_KEYS.includes(s.key) ? SERVICE_META[s.key] : { soft: "", bar: "" };
    SERVICE_META[s.key] = { ...base, label: s.label, short: DEFAULT_KEYS.includes(s.key) && s.label === base.label ? base.short : s.label, color: s.color || base.color || "#64748b", singular: s.singular || s.label, custom: !DEFAULT_KEYS.includes(s.key) };
  }
  SERVICE_KEYS.splice(0, SERVICE_KEYS.length, ...list.map((s) => s.key));
}
const OTHER = { label: "Other", short: "Other", color: "#94a3b8", soft: "bg-slate-100 text-slate-600 ring-slate-200", bar: "bg-slate-400" };
export const serviceMeta = (k) => SERVICE_META[k] || OTHER;
/** className + style for a service-coloured chip (custom services have no Tailwind palette). */
export function chipProps(k, extra = "") {
  const m = serviceMeta(k);
  if (m.soft && !m.custom) return { className: `ring-1 ring-inset ${m.soft} ${extra}` };
  return { className: `ring-1 ring-inset ${extra}`, style: { backgroundColor: `${m.color}14`, color: m.color, "--tw-ring-color": `${m.color}40` } };
}

// Task statuses (superset kept in sync with the server).
export const STATUS_META = {
  planned: { label: "Planned", chip: "bg-slate-100 text-slate-700 ring-slate-200", bucket: "pending" },
  assigned: { label: "Assigned", chip: "bg-blue-50 text-blue-700 ring-blue-200", bucket: "pending" },
  in_progress: { label: "In Progress", chip: "bg-indigo-50 text-indigo-700 ring-indigo-200", bucket: "in_progress" },
  internal_review: { label: "Internal Review", chip: "bg-violet-50 text-violet-700 ring-violet-200", bucket: "in_progress" },
  client_review: { label: "Client Review", chip: "bg-cyan-50 text-cyan-700 ring-cyan-200", bucket: "in_progress" },
  revision_required: { label: "Revision Required", chip: "bg-orange-50 text-orange-700 ring-orange-200", bucket: "in_progress" },
  approved: { label: "Approved", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200", bucket: "completed" },
  completed: { label: "Completed", chip: "bg-green-50 text-green-700 ring-green-200", bucket: "completed" },
  on_hold: { label: "On Hold", chip: "bg-amber-50 text-amber-700 ring-amber-200", bucket: "pending" },
};
const LEGACY_BUCKET = { pending: "pending", to_do: "pending", todo: "pending", doing: "in_progress", review: "in_progress", pending_approval: "in_progress", done: "completed", cancelled: "cancelled", canceled: "cancelled" };
export const statusMeta = (s) => STATUS_META[s] || { label: (s || "Planned").replace(/_/g, " "), chip: "bg-slate-100 text-slate-700 ring-slate-200", bucket: LEGACY_BUCKET[s] || "pending" };
export const bucketOf = (s) => statusMeta(s).bucket;
export const STATUS_KEYS = Object.keys(STATUS_META);

export const PRIORITIES = [
  ["low", "Low", "bg-slate-100 text-slate-600"],
  ["medium", "Medium", "bg-blue-50 text-blue-700"],
  ["high", "High", "bg-orange-50 text-orange-700"],
  ["urgent", "Urgent", "bg-rose-50 text-rose-700"],
];

// ── dates (IST) ──
export const istToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
export const thisMonth = () => istToday().slice(0, 7);
export const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};
export const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
export const daysInMonth = (ym) => new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0).getDate();
export const dayKey = (ym, d) => `${ym}-${String(d).padStart(2, "0")}`;
export const fmtDay = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");
export const weekdayShort = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" }) : "");
export const isSunday = (iso) => new Date(`${String(iso).slice(0, 10)}T00:00:00`).getDay() === 0;

/** Week buckets (1-7, 8-14, …) used by the planning board. */
export function weeksOf(ym) {
  const total = daysInMonth(ym);
  const out = [];
  for (let start = 1; start <= total; start += 7) {
    const end = Math.min(total, start + 6);
    out.push({ index: out.length + 1, label: `Week ${out.length + 1}`, from: dayKey(ym, start), to: dayKey(ym, end), range: `${start}–${end}` });
  }
  return out;
}
export const weekIndexOf = (iso, ym) => {
  if (!iso || String(iso).slice(0, 7) !== ym) return null;
  return Math.floor((Number(String(iso).slice(8, 10)) - 1) / 7) + 1;
};

export const pctOf = (done, base) => (base > 0 ? Math.round((done / base) * 100) : 0);
export const clampPct = (p) => Math.max(0, Math.min(100, p || 0));
export const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

export function ProgressBar({ pct, color = "#16a34a", height = "h-2" }) {
  return (
    <div className={`w-full overflow-hidden rounded-full bg-slate-100 ${height}`}>
      <div className="h-full rounded-full transition-all" style={{ width: `${clampPct(pct)}%`, backgroundColor: color }} />
    </div>
  );
}

export function ServiceChip({ service, className = "" }) {
  const m = serviceMeta(service);
  const p = chipProps(service, `inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${className}`);
  return <span {...p}>{m.short}</span>;
}

export function StatusChip({ status }) {
  const m = statusMeta(status);
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${m.chip}`}>{m.label}</span>;
}

/** Overdue = not finished and the due date has passed. */
export const isOverdue = (task, today = istToday()) => {
  const b = bucketOf(task.status);
  const d = task.due_date ? String(task.due_date).slice(0, 10) : "";
  return b !== "completed" && b !== "cancelled" && !!d && d < today;
};
